import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Globe, Save, Edit, Loader2, DollarSign } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function AdminExchangeRatesTab() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [editingRate, setEditingRate] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchRates(); }, []);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exchange-rates`, { headers });
      setRates(res.data || []);
    } catch {
      const defaults = [];
      for (let year = 2025; year <= 2026; year++)
        for (let month = 1; month <= 12; month++)
          defaults.push({ year, month, rate: year === 2025 ? 4.4 : 4.2, is_default: true });
      setRates(defaults);
    } finally { setLoading(false); }
  };

  const saveRate = async (year, month, rate) => {
    try {
      await axios.post(`${API}/exchange-rates`, { year, month, rate }, { headers });
      toast.success(`Rate for ${MONTHS[month-1]} ${year} updated to ${rate}`);
      setRates(prev => {
        const idx = prev.findIndex(r => r.year === year && r.month === month);
        if (idx >= 0) { const u = [...prev]; u[idx] = { ...u[idx], rate, is_default: false }; return u; }
        return [...prev, { year, month, rate, is_default: false }];
      });
      setEditingRate(null);
    } catch { toast.error('Failed to save rate'); }
  };

  const yearRates = MONTHS.map((m, i) => {
    const found = rates.find(r => r.year === selectedYear && r.month === i + 1);
    return { month: i + 1, name: m, rate: found?.rate ?? (selectedYear === 2025 ? 4.4 : 4.2), is_default: found?.is_default !== false };
  });

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[hsl(var(--primary))]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center"><Globe size={20} className="text-white" /></div>
          <div>
            <h2 className="text-lg font-bold text-white">USD/MYR Exchange Rates</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">Monthly exchange rates for IB commission calculations</p>
          </div>
        </div>
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-28 bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <SelectItem value="2025">2025</SelectItem>
            <SelectItem value="2026">2026</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 lg:grid-cols-4 gap-4">
        {yearRates.map(({ month, name, rate, is_default }) => (
          <div key={month} className={`bg-[hsl(var(--surface))] border rounded-xl p-4 transition-all ${is_default ? 'border-[hsl(var(--border))]' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[hsl(var(--text-secondary))]">{name}</span>
              <Badge className={is_default ? 'bg-[hsl(var(--background))] text-[hsl(var(--text-muted))] text-[9px]' : 'bg-emerald-500/20 text-emerald-400 text-[9px]'}>
                {is_default ? 'Default' : 'Custom'}
              </Badge>
            </div>
            {editingRate === `${selectedYear}-${month}` ? (
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" defaultValue={rate} className="bg-[hsl(var(--background))] border-emerald-500/30 text-white h-8 text-sm w-20" id={`rate-${month}`} onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRate(selectedYear, month, parseFloat(e.target.value));
                  if (e.key === 'Escape') setEditingRate(null);
                }} autoFocus />
                <Button size="sm" className="h-8 w-8 p-0 bg-emerald-600" onClick={() => { const v = document.getElementById(`rate-${month}`).value; saveRate(selectedYear, month, parseFloat(v)); }}><Save size={12} /></Button>
              </div>
            ) : (
              <div className="flex items-center justify-between cursor-pointer group" onClick={() => setEditingRate(`${selectedYear}-${month}`)}>
                <div className="flex items-center gap-1">
                  <DollarSign size={14} className="text-emerald-400" />
                  <span className="text-xl font-bold text-white font-mono">{rate?.toFixed(2)}</span>
                </div>
                <Edit size={14} className="text-[hsl(var(--text-muted))] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
